import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { PortalApiService } from '../../core/portal-api.service';
import { PortalAuthService } from '../../core/portal-auth.service';
import { PortalStateService } from '../../core/portal-state.service';
import { I18nService } from '../../core/i18n.service';
import { EmployeeDialogComponent } from './employee-dialog.component';
import { LedgerDialogComponent } from './ledger-dialog.component';
import { ConfirmDialogComponent } from '../../core/confirm-dialog.component';

// The reason the portal exists: let the employer add a new hire and cut off a
// leaver themselves, without a support request to Frigg.
@Component({
  selector: 'portal-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.css']
})
export class EmployeesComponent implements OnInit, OnDestroy {

  loading = true;
  error = '';
  employees: any[] = [];
  search = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';
  groupFilter = '';
  busy: { [membershipId: string]: boolean } = {};

  // Derived state in FIELDS, recomputed by recompute(). Deliberately not
  // getters: a getter bound to *ngFor re-runs on every change-detection pass,
  // and any getter that mints new objects sends Angular into an unbounded
  // rebuild loop (see core/months.ts). Fields also avoid re-filtering the whole
  // employee list dozens of times a second.
  groups: any[] = [];
  filtered: any[] = [];
  activeCount = 0;

  private sub: Subscription;

  constructor(
    private api: PortalApiService,
    private auth: PortalAuthService,
    private state: PortalStateService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private i18n: I18nService
  ) { }

  ngOnInit() {
    this.sub = this.state.customerId$.subscribe(() => {
      this.refreshGroups();
      this.load();
    });
  }

  ngOnDestroy() { if (this.sub) { this.sub.unsubscribe(); } }

  get canEdit(): boolean {
    const s = this.auth.session;
    return !!(s && s.user && s.user.rights && s.user.rights.writeEmployees);
  }

  // Only groups belonging to the currently selected company, so the "add
  // employee" form can't offer a group the employee would then be moved out of.
  private refreshGroups() {
    const s = this.auth.session;
    const scope = this.state.customerId;
    this.groups = ((s && s.groups) || []).filter((g: any) => !scope || g.customerId === scope);
  }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const res: any = await this.api.employees(this.state.customerId || undefined);
      this.employees = (res && res.employees) || [];
    } catch (err: any) {
      this.error = (err && err.error && err.error.message) || this.i18n.t('employees.errorLoadFailed');
      this.employees = [];
    }
    this.recompute();
    this.loading = false;
  }

  // Called on load and whenever a filter or the search box changes.
  recompute() {
    const term = this.search.trim().toLowerCase();
    this.filtered = this.employees.filter(e => {
      if (this.statusFilter === 'active' && !e.status) { return false; }
      if (this.statusFilter === 'inactive' && e.status) { return false; }
      if (this.groupFilter && e.groupDocId !== this.groupFilter) { return false; }
      if (!term) { return true; }
      return (e.name || '').toLowerCase().includes(term)
        || (e.email || '').toLowerCase().includes(term)
        || (e.employeeCode || '').toLowerCase().includes(term);
    });
    this.activeCount = this.employees.filter(e => e.status).length;
  }

  add() {
    if (!this.groups.length) {
      this.snack.open(this.i18n.t('employees.errorNoGroup'), 'OK', { duration: 6000 });
      return;
    }
    const ref = this.dialog.open(EmployeeDialogComponent, {
      width: '480px',
      data: { mode: 'add', groups: this.groups }
    });
    ref.afterClosed().subscribe(async result => {
      if (!result) { return; }
      try {
        const res: any = await this.api.addEmployee(result);
        if (res && res.success) {
          this.snack.open(this.i18n.t(res.invited ? 'employees.successAddedInvited' : 'employees.successAddedNoInvite'),
            'OK', { duration: 6000 });
          await this.load();
        } else {
          this.snack.open((res && res.message) || this.i18n.t('employees.errorAddFailed'), 'OK', { duration: 6000 });
        }
      } catch (err: any) {
        const body = err && err.error;
        // A duplicate is the common failure; point at the row that blocked it
        // rather than making them hunt for it.
        if (body && body.existing) {
          this.search = body.existing.email || body.existing.name || '';
          this.statusFilter = 'all';
          this.recompute();
          this.snack.open(
            this.i18n.t('employees.errorDuplicate',
              { name: body.existing.name || this.i18n.t('employees.thatPerson'), group: body.existing.groupName }),
            'OK', { duration: 7000 });
        } else {
          this.snack.open((body && body.message) || this.i18n.t('employees.errorAddFailed'), 'OK', { duration: 6000 });
        }
      }
    });
  }

  edit(employee: any) {
    const ref = this.dialog.open(EmployeeDialogComponent, {
      width: '480px',
      data: { mode: 'edit', groups: this.groups, employee }
    });
    ref.afterClosed().subscribe(async patch => {
      if (!patch) { return; }
      this.busy[employee.membershipId] = true;
      try {
        await this.api.updateEmployee(employee.membershipId, patch);
        this.snack.open(this.i18n.t('employees.successSaved'), 'OK', { duration: 3000 });
        await this.load();
      } catch (err: any) {
        this.snack.open((err && err.error && err.error.message) || this.i18n.t('employees.errorSaveFailed'), 'OK', { duration: 6000 });
      }
      this.busy[employee.membershipId] = false;
    });
  }

  async setStatus(employee: any, status: boolean) {
    if (!status) {
      const ok = await this.confirm(
        this.i18n.t('employees.confirmDeactivateTitle', { name: employee.name || this.i18n.t('employees.thisEmployee') }),
        this.i18n.t('employees.confirmDeactivateMessage'),
        this.i18n.t('employees.confirmDeactivateLabel'));
      if (!ok) { return; }
    }
    this.busy[employee.membershipId] = true;
    try {
      await this.api.setStatus(employee.membershipId, status);
      employee.status = status;
      this.recompute();
      this.snack.open(this.i18n.t(status ? 'employees.successReactivated' : 'employees.successDeactivated'), 'OK', { duration: 4000 });
    } catch (err: any) {
      this.snack.open((err && err.error && err.error.message) || this.i18n.t('employees.errorStatusFailed'), 'OK', { duration: 6000 });
    }
    this.busy[employee.membershipId] = false;
  }

  async remove(employee: any) {
    const ok = await this.confirm(
      this.i18n.t('employees.confirmRemoveTitle', { name: employee.name || this.i18n.t('employees.thisEmployee') }),
      this.i18n.t('employees.confirmRemoveMessage'),
      this.i18n.t('employees.confirmRemoveLabel'));
    if (!ok) { return; }

    this.busy[employee.membershipId] = true;
    try {
      await this.api.removeEmployee(employee.membershipId, 'Removed via customer portal');
      this.snack.open(this.i18n.t('employees.successRemoved'), 'OK', { duration: 4000 });
      await this.load();
    } catch (err: any) {
      this.snack.open((err && err.error && err.error.message) || this.i18n.t('employees.errorRemoveFailed'), 'OK', { duration: 6000 });
    }
    this.busy[employee.membershipId] = false;
  }

  async sendReset(employee: any) {
    this.busy[employee.membershipId] = true;
    try {
      await this.api.resetEmployeePassword(employee.membershipId);
      this.snack.open(this.i18n.t('employees.successResetSent', { email: employee.email }), 'OK', { duration: 5000 });
    } catch (err: any) {
      this.snack.open((err && err.error && err.error.message) || this.i18n.t('employees.errorResetFailed'), 'OK', { duration: 6000 });
    }
    this.busy[employee.membershipId] = false;
  }

  openLedger(employee: any) {
    this.dialog.open(LedgerDialogComponent, { width: '560px', data: { employee } });
  }

  exportCsv() {
    const rows = [[
      this.i18n.t('employees.colName'), this.i18n.t('employees.colEmail'), this.i18n.t('employees.colEmployeeNo'),
      this.i18n.t('employees.colGroup'), this.i18n.t('employees.colCredit'), this.i18n.t('employees.colStatus')
    ]];
    this.filtered.forEach(e => rows.push([
      e.name || '', e.email || '', e.employeeCode || '', e.groupName || '',
      String(e.credit || 0), this.i18n.t(e.status ? 'employees.active' : 'employees.deactivated')
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    // BOM so Excel opens Norwegian characters correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'employees.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private confirm(title: string, message: string, confirmLabel: string): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: { title, message, confirmLabel }
    });
    return ref.afterClosed().toPromise().then(v => v === true);
  }

  kr(v: number): string {
    return (Number(v) || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
